/**
 * Ficha em XML no formato do NDB do original (ExportToXMLString /
 * ImportXMLString), para levar fichas de um lado para o outro.
 *
 * - `<database>` é o próprio nó exportado; cada filho vira um elemento com o nome
 *   do nó; os valores ficam em atributos XML.
 * - Cada elemento termina com `<__ndb__ id="…" t_<nome>="…"/>`: o id do nó e o
 *   tipo de cada valor (X vazio, S texto, I inteiro, 6 inteiro 64, D real,
 *   B lógico True/False, N binário em base64). A raiz leva `idGenerator`.
 * - Nomes que não são identificadores viram `__EHEX_` + hex do UTF-8 (na
 *   leitura também `__E64_` + base64).
 *
 * Detalhes em docs/spec/ndb-xml.md.
 */
import type { AttrValue, NodeId, Op } from './types.js';
import type { NodeTree, TreeNode } from './tree.js';

/* ------------------------------------------------------------------ nomes */

const IDENT = /^[A-Za-z_][A-Za-z0-9_]*$/;

export function encodeXmlName(name: string): string {
  if (IDENT.test(name)) return name;
  return `__EHEX_${[...new TextEncoder().encode(name)].map((b) => b.toString(16).toUpperCase().padStart(2, '0')).join('')}`;
}

export function decodeXmlName(name: string): string {
  if (name.startsWith('__E64_')) {
    const bin = atob(name.slice(6));
    return new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)));
  }
  if (name.startsWith('__EHEX_')) {
    const hex = name.slice(7);
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < bytes.length; i++) bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    return new TextDecoder().decode(bytes);
  }
  return name;
}

/* ------------------------------------------------------------------ exportar */

const escapeAttr = (v: string) =>
  v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/\r/g, '&#13;').replace(/\n/g, '&#10;').replace(/\t/g, '&#9;');

function typeOf(v: AttrValue): { code: string; text: string } {
  if (typeof v === 'boolean') return { code: 'B', text: v ? 'True' : 'False' };
  if (typeof v === 'number') {
    if (Number.isInteger(v)) return { code: Math.abs(v) <= 2147483647 ? 'I' : '6', text: String(v) };
    return { code: 'D', text: String(v) };
  }
  return { code: 'S', text: v };
}

/** O nó (e tudo abaixo dele) como XML do NDB. */
export function exportNodeToXml(tree: NodeTree, nodeId: NodeId): string {
  const root = tree.get(nodeId);
  if (!root) throw new Error(`nó ${nodeId} não existe`);
  let next = 0;
  const write = (node: TreeNode, element: string, isRoot: boolean): string => {
    const id = ++next;
    const attrs: string[] = [];
    const types: string[] = [];
    for (const [name, value] of node.attrs) {
      const key = encodeXmlName(name);
      const { code, text } = typeOf(value);
      attrs.push(` ${key}="${escapeAttr(text)}"`);
      types.push(` t_${key}="${code}"`);
    }
    const children = node.children.map((c) => write(c, encodeXmlName(c.name), false)).join('');
    const meta = `<__ndb__ id="${id}"${isRoot ? ` idGenerator="${'%IDGEN%'}"` : ''}${types.join('')}/>`;
    return `<${element}${attrs.join('')}>${children}${meta}</${element}>`;
  };
  const body = write(root, 'database', true);
  return `<?xml version="1.0" encoding="UTF-8"?>\n${body.replace('%IDGEN%', String(next))}`;
}

/* ------------------------------------------------------------------ ler XML */

type XmlElement = { name: string; attrs: Map<string, string>; children: XmlElement[] };

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };

function decodeEntities(s: string): string {
  return s.replace(/&(#x[0-9a-fA-F]+|#[0-9]+|[a-zA-Z]+);/g, (whole, ent: string) => {
    if (ent[0] === '#') {
      const code = ent[1] === 'x' || ent[1] === 'X' ? parseInt(ent.slice(2), 16) : parseInt(ent.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : whole;
    }
    return ENTITIES[ent] ?? whole;
  });
}

/** Leitor de XML pequeno (elementos, atributos, entidades, comentários): o que o formato usa. */
export function parseXml(text: string): XmlElement {
  let i = 0;
  const src = text.replace(/^﻿/, '');
  const fail = (msg: string): never => {
    throw new Error(`XML inválido (posição ${i}): ${msg}`);
  };
  const skipMisc = () => {
    for (;;) {
      while (i < src.length && /\s/.test(src[i]!)) i++;
      if (src.startsWith('<?', i)) {
        const end = src.indexOf('?>', i);
        if (end < 0) fail('declaração sem fim');
        i = end + 2;
      } else if (src.startsWith('<!--', i)) {
        const end = src.indexOf('-->', i);
        if (end < 0) fail('comentário sem fim');
        i = end + 3;
      } else if (src.startsWith('<!', i)) {
        const end = src.indexOf('>', i);
        if (end < 0) fail('declaração sem fim');
        i = end + 1;
      } else return;
    }
  };
  const readName = () => {
    const m = /^[A-Za-z_:][-A-Za-z0-9_:.]*/.exec(src.slice(i, i + 512));
    if (!m) fail('nome esperado');
    i += m![0].length;
    return m![0];
  };
  const readElement = (): XmlElement => {
    if (src[i] !== '<') fail('"<" esperado');
    i++;
    const name = readName();
    const attrs = new Map<string, string>();
    for (;;) {
      while (/\s/.test(src[i] ?? '')) i++;
      if (src.startsWith('/>', i)) {
        i += 2;
        return { name, attrs, children: [] };
      }
      if (src[i] === '>') {
        i++;
        break;
      }
      const attr = readName();
      while (/\s/.test(src[i] ?? '')) i++;
      if (src[i] !== '=') fail('"=" esperado');
      i++;
      while (/\s/.test(src[i] ?? '')) i++;
      const quote = src[i];
      if (quote !== '"' && quote !== "'") fail('aspas esperadas');
      const end = src.indexOf(quote!, i + 1);
      if (end < 0) fail('valor sem fim');
      attrs.set(attr, decodeEntities(src.slice(i + 1, end)));
      i = end + 1;
    }
    const children: XmlElement[] = [];
    for (;;) {
      // texto entre elementos não é usado pelo formato
      const lt = src.indexOf('<', i);
      if (lt < 0) fail(`</${name}> esperado`);
      i = lt;
      if (src.startsWith('<!--', i)) {
        const end = src.indexOf('-->', i);
        if (end < 0) fail('comentário sem fim');
        i = end + 3;
        continue;
      }
      if (src.startsWith('</', i)) {
        i += 2;
        const closing = readName();
        if (closing !== name) fail(`</${name}> esperado, veio </${closing}>`);
        while (/\s/.test(src[i] ?? '')) i++;
        if (src[i] !== '>') fail('">" esperado');
        i++;
        return { name, attrs, children };
      }
      children.push(readElement());
    }
  };
  skipMisc();
  const root = readElement();
  return root;
}

/* ------------------------------------------------------------------ importar */

function valueOf(text: string, code: string | undefined): AttrValue | null {
  switch (code ?? 'S') {
    case 'X':
      return null;
    case 'I':
    case '6': {
      const n = Number.parseInt(text, 10);
      return Number.isFinite(n) ? n : 0;
    }
    case 'D': {
      const n = Number(text.replace(',', '.'));
      return Number.isFinite(n) ? n : 0;
    }
    case 'B':
      return /^(true|1|-1)$/i.test(text.trim());
    default:
      // S, N (binário fica como o texto base64) e tipos desconhecidos
      return text;
  }
}

/**
 * Operações que trocam o conteúdo do nó `target` pelo XML: limpa o nó e recria
 * valores e filhos. `newId` gera os ids dos nós novos (os do XML não valem aqui).
 * Devolve em pedaços de até `chunk` operações (o servidor aceita lotes limitados).
 */
export function importXmlOps(xml: string, target: NodeId, newId: () => NodeId, chunk = 1500): Op[][] {
  const root = parseXml(xml);
  const ops: Op[] = [{ t: 'clear', node: target }];
  const fill = (el: XmlElement, node: NodeId) => {
    const meta = el.children.find((c) => c.name === '__ndb__');
    for (const [key, text] of el.attrs) {
      const value = valueOf(text, meta?.attrs.get(`t_${key}`));
      if (value !== null) ops.push({ t: 'set', node, name: decodeXmlName(key), value });
    }
    for (const child of el.children) {
      if (child.name === '__ndb__' || child.name.startsWith('__ndb')) continue;
      const id = newId();
      ops.push({ t: 'add', parent: node, id, name: decodeXmlName(child.name) });
      fill(child, id);
    }
  };
  fill(root, target);
  const out: Op[][] = [];
  for (let k = 0; k < ops.length; k += chunk) out.push(ops.slice(k, k + chunk));
  return out;
}
